import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import vacuumCleanerSvg from './vacuum-cleaner.svg';

// --- Configuration ---
const BASE_VACUUM_SIZE = 80; // Base size for desktop, will be responsive
const MOBILE_VACUUM_SIZE = 60; // Size for mobile devices

// Define vacuum positions
const VACUUM_START_X = 8; // Upper left X position as percentage - moved closer to corner
const VACUUM_START_Y = 0; // Upper left Y position as percentage - moved closer to corner
const SAFE_ZONE_RADIUS = 12; // percentage radius to keep clear of phrases

// Phrase configuration
const MIN_DISTANCE_BETWEEN_PHRASES = 10; // Minimum distance between phrases in percentage
const PHRASE_MARGIN_EDGE = 18; // Keep phrases this percentage away from edges (increased)
const MAX_RIGHT_POSITION = 75; // Maximum right position as percentage (to prevent overflow)

// --- Final Text Lines ---
const FINAL_LINES = [
  "I\'m a vacuum cleaner.",
  "Pulling in the dirt and grime someone left behind,",
  "The more dust on the book the better.",
  "The more grit on the film the better.",
  "The more gunk in the pulp the better.",
  "Histories dust bunnies with tongues crammed into their cheeks,",
  "Earnestly sharing their slice of time and space,",
  "become fodder to be crushed into diamonds,",
  "eventually.",
  "Or they simply make me smile.",
];
const TOTAL_LINES = FINAL_LINES.length;

// --- Initial Phrases Data ---
const phrases = [
  "Jimbo the Drag Clown",
  "In a Lonely Place",
  "The Unbearable Bassington",
  "Female Trouble",
  "The Sopranos",
  "The Revolt of Mamie Stover",
  "Ace in the Hole",
  "The Big Clock",
  "To Be or Not to Be",
  "The Sea Wolf",
  "Dorothy Parker",
  "Ogden Nash",
  "The Promiscuous Doll",
  "Venus on the Half Shell",
  "The Naked Kiss",
  "La Cage Aux Folles",
  "Harold and Maude",
  "Sipsworth",
  "An Elderly Lady is Up to No Good",
  "A Psalm for the Wild-Built",
  "Black Flags Over Blue Waters",
  "What About Bob?",
  "End of Eternity",
  "Sirens of Titan",
  "The Great Dictator",
  "The Pez Outlaw",
  "The Natural History of Chickens",
  "The Rehearsal",
  "Martin Eden",
  "Alfie",
  "Mon Oncle",
  "The Ruling Class",
  "Sweeney Todd: The Demon Barber of Fleet Street",
  "The Man Who Would Be King",
  "The Importance of Being Earnest",
  "Glen or Glenda",
  "Pushing Daisies",
  "The Big Heat"
];
const TOTAL_PHRASES = phrases.length; // Total number of phrases available

// Calculate phrases needed per line with even distribution
const PHRASES_PER_LINE = Math.ceil(TOTAL_PHRASES / TOTAL_LINES);

// Create initial phrase data with positioning logic to avoid vacuum center and prevent overlaps
const initialPhrasesData = (() => {
  const result = [];
  
  for (let i = 0; i < TOTAL_PHRASES; i++) {
    // Generate potential position
    let x, y;
    let isValid = false;
    let attempts = 0;
    const maxAttempts = 20; // Prevent infinite loops
    
    // Keep generating positions until we find one that's valid
    while (!isValid && attempts < maxAttempts) {
      attempts++;
      
      // Use an area division approach for more even distribution
      // Divide the available space into three columns and multiple rows
      const numColumns = 3;
      const numRows = Math.ceil(TOTAL_PHRASES / numColumns);
      
      // Calculate which area this phrase should be in
      const areaRow = Math.floor(i / numColumns);
      const areaCol = i % numColumns;
      
      // Calculate the base position within that area (with padding)
      // We'll use a more conservative width range to keep elements from going off screen
      const areaWidth = (MAX_RIGHT_POSITION - PHRASE_MARGIN_EDGE) / numColumns;
      const areaHeight = (80 - PHRASE_MARGIN_EDGE * 2) / numRows;
      
      // Base position is center of the assigned area
      const baseX = PHRASE_MARGIN_EDGE + (areaCol * areaWidth) + (areaWidth / 2);
      const baseY = PHRASE_MARGIN_EDGE + (areaRow * areaHeight) + (areaHeight / 2);
      
      // Add randomness within the area, but not too much
      const randomOffsetX = (Math.random() * areaWidth * 0.6) - (areaWidth * 0.3);
      const randomOffsetY = (Math.random() * areaHeight * 0.6) - (areaHeight * 0.3);
      
      x = baseX + randomOffsetX;
      y = baseY + randomOffsetY;
      
      // Constrain to safe viewport bounds
      x = Math.max(PHRASE_MARGIN_EDGE, Math.min(MAX_RIGHT_POSITION, x));
      y = Math.max(PHRASE_MARGIN_EDGE, Math.min(80 - PHRASE_MARGIN_EDGE, y));
      
      // Check if this position is in the vacuum safe zone
      const distanceToVacuum = Math.sqrt(
        Math.pow(x - VACUUM_START_X, 2) + 
        Math.pow(y - VACUUM_START_Y, 2)
      );
      
      // Check if this position is far enough from other phrases
      let tooCloseToOtherPhrase = false;
      
      // Check against previously placed phrases in our result array
      for (let j = 0; j < result.length; j++) {
        const other = result[j];
        const distanceToOther = Math.sqrt(
          Math.pow(x - other.x, 2) + 
          Math.pow(y - other.y, 2)
        );
        if (distanceToOther < MIN_DISTANCE_BETWEEN_PHRASES) {
          tooCloseToOtherPhrase = true;
          break;
        }
      }
      
      isValid = distanceToVacuum >= SAFE_ZONE_RADIUS && !tooCloseToOtherPhrase;
    }
    
    // If we couldn't find a valid position after max attempts,
    // use a simplified fallback approach with absolute guarantees for position
    if (!isValid) {
      // Divide the available area into a simple grid
      const fallbackCols = 3;
      const fallbackRows = Math.ceil(TOTAL_PHRASES / fallbackCols);
      
      const col = i % fallbackCols;
      const row = Math.floor(i / fallbackCols);
      
      // Calculate position with guaranteed margins
      x = PHRASE_MARGIN_EDGE + col * ((MAX_RIGHT_POSITION - PHRASE_MARGIN_EDGE) / fallbackCols);
      y = PHRASE_MARGIN_EDGE + row * ((80 - PHRASE_MARGIN_EDGE * 2) / fallbackRows);
      
      // Add a small random offset
      x += (Math.random() * 5) - 2.5;
      y += (Math.random() * 5) - 2.5;
    }
    
    result.push({
      id: i + 1,
      text: phrases[i] || `phrase ${i + 1}`,
      x,
      y,
    });
  }
  
  return result;
})();

// --- Helper Function ---
const doRectsOverlap = (rect1, rect2) => {
  if (!rect1 || !rect2) return false;
  return !(
    rect1.right < rect2.left ||
    rect1.left > rect2.right ||
    rect1.bottom < rect2.top ||
    rect1.top > rect2.bottom
  );
};

// --- Custom Hook for Viewport ---
function useViewport() {
  const [width, setWidth] = useState(window.innerWidth);
  const [height, setHeight] = useState(window.innerHeight);
  
  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
      setHeight(window.innerHeight);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return { width, height };
}

// --- Phrase Component ---
function Phrase({ id, text, initialX, initialY, phraseRef, isMobile, isBeingCollected }) {
  const phraseVariants = {
    initial: { opacity: 0, scale: 0.8 },
    animate: { 
      opacity: 1, 
      scale: 1, 
      transition: { 
        duration: 0.6,
        ease: "easeOut" 
      } 
    },
    exit: { 
      opacity: 0, 
      scale: 0.1, 
      rotate: -15,
      transition: { 
        duration: 0.4, 
        ease: "easeIn" 
      } 
    },
    hover: {
      y: -3,
      x: 1,
      scale: 1.03,
      transition: { duration: 0.3, type: "spring", stiffness: 300 }
    }
  };

  // Generate refined color palette for phrases
  const dirtColors = [
    '#59413D', // deep umber
    '#614E3E', // rich cocoa
    '#605850', // slate gray-brown
    '#5E4B45', // burnt sienna
    '#4E453F', // charcoal brown
    '#524A41', // dark taupe
    '#614F3E', // tobacco brown
    '#50413A', // dark mocha
  ];
  
  const colorIndex = id % dirtColors.length;
  const backgroundColor = dirtColors[colorIndex];
  
  // Subtle rotation for visual interest
  const rotation = ((id * 17) % 7) - 3.5; // -3.5 to +3.5 degrees
  
  // Increase max width for more space
  const maxWidth = isMobile ? '140px' : '200px';

  // More refined irregular border
  const borderRadius = `${4 + (id % 6)}px ${7 + (id % 5)}px ${3 + ((id * 3) % 6)}px ${6 + ((id * 7) % 5)}px`;
  
  return (
    <motion.div
      ref={phraseRef}
      layout
      variants={phraseVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      whileHover="hover"
      className="absolute px-4 py-3 pointer-events-none font-serif"
      style={{ 
        left: `${initialX}%`, 
        top: `${initialY}%`,
        transform: `translate(-50%, -50%) rotate(${rotation}deg)`, // Center the phrase
        background: backgroundColor,
        color: '#FFF8EA', // warm ivory for better contrast
        borderRadius: borderRadius,
        maxWidth: maxWidth,
        fontSize: isMobile ? '0.8rem' : '0.95rem',
        fontWeight: '500',
        lineHeight: '1.6',
        letterSpacing: '0.02em',
        boxShadow: `0 3px 6px rgba(0,0,0,0.25), inset 0 1px 2px rgba(255,255,255,0.15)`,
        textShadow: '0px 1px 1px rgba(0,0,0,0.6)',
        filter: 'contrast(1.05) brightness(1)',
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 250 250\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")',
        backgroundBlendMode: 'soft-light',
        backgroundSize: '150px',
        wordWrap: 'break-word',
        wordBreak: 'normal',
        hyphens: 'auto',
        zIndex: 5,
        opacity: isBeingCollected ? 0.5 : 1,
        fontFamily: '"Georgia", "Times New Roman", serif'
      }}
    >
      {text}
    </motion.div>
  );
}

// --- Draggable Vacuum Component ---
function DraggableVacuum({ vacuumRef, constraintsRef, onDrag, isMobile, isCollecting }) {
  // Animation variants for the vacuum
  const vacuumVariants = {
    initial: {
      scale: 1,
      rotate: 0
    },
    animate: {
      scale: [1, 1.05, 1],
      rotate: [0, -2, 2, 0],
      filter: ["drop-shadow(0px 4px 6px rgba(0,0,0,0.2))", "drop-shadow(0px 6px 8px rgba(0,0,0,0.3))", "drop-shadow(0px 4px 6px rgba(0,0,0,0.2))"],
      transition: {
        scale: {
          repeat: Infinity,
          repeatType: "reverse",
          duration: 2.5,
          ease: "easeInOut"
        },
        rotate: {
          repeat: Infinity,
          repeatType: "reverse",
          duration: 3.5,
          ease: "easeInOut"
        },
        filter: {
          repeat: Infinity,
          repeatType: "reverse",
          duration: 2.5,
          ease: "easeInOut"
        }
      }
    },
    hover: {
      scale: 1.12,
      filter: "drop-shadow(0px 8px 10px rgba(0,0,0,0.35))",
      transition: {
        duration: 0.3
      }
    },
    tap: {
      scale: 0.95,
      transition: {
        duration: 0.15
      }
    }
  };

  const vacuumSize = isMobile ? MOBILE_VACUUM_SIZE : BASE_VACUUM_SIZE;

  return (
    <motion.div
      ref={vacuumRef}
      drag
      dragConstraints={constraintsRef}
      dragElastic={0.1}
      dragMomentum={false}
      onDrag={onDrag}
      variants={vacuumVariants}
      initial="initial"
      animate="animate"
      whileHover="hover"
      whileTap="tap"
      className="absolute z-10 cursor-grab active:cursor-grabbing touch-none"
      style={{ 
        width: vacuumSize, 
        height: vacuumSize,
        // Position the vacuum in the upper left
        left: `calc(${VACUUM_START_X}% - ${vacuumSize/2}px)`, 
        top: `${VACUUM_START_Y}%`,
        opacity: isCollecting ? 0.5 : 1,
        filter: "drop-shadow(0px 4px 6px rgba(0,0,0,0.2))"
      }}
    >
      <img 
        src={vacuumCleanerSvg} 
        alt="Vacuum Cleaner" 
        className="w-full h-full pointer-events-none select-none"
        draggable="false"
      />
    </motion.div>
  );
}

// --- Main App Component ---
function VacuumPoem() {
  const [phrases, setPhrases] = useState(
    initialPhrasesData.map(p => ({ ...p, isSucked: false }))
  );
  const [collecting, setCollecting] = useState(false);
  const [lastCollectedId, setLastCollectedId] = useState(null);
  const [previousSuckedCount, setPreviousSuckedCount] = useState(0); // Track phrase count change
  const [animatingLineIndex, setAnimatingLineIndex] = useState(null); // Track which line is currently animating
  const containerRef = useRef(null);
  const vacuumRef = useRef(null);
  const phraseRefs = useRef(new Map());
  const collectionTimeoutRef = useRef(null);
  const { width, height } = useViewport();
  
  // Check if mobile based on viewport width
  const isMobile = width < 768; // Standard breakpoint for tablets and below

  // Manage phrase refs
  phrases.forEach(phrase => {
    if (!phrase.isSucked && !phraseRefs.current.has(phrase.id)) {
      phraseRefs.current.set(phrase.id, React.createRef());
    } else if (phrase.isSucked && phraseRefs.current.has(phrase.id)) {
      phraseRefs.current.delete(phrase.id);
    }
  });

  // Handler for when a phrase is sucked
  const handlePhraseSucked = useCallback((id) => {
    // Don't process if we're already collecting
    if (collecting) return;
    
    // Set collecting state to prevent multiple collections at once
    setCollecting(true);
    setLastCollectedId(id);
    
    // Add a slight delay to make the collection more visual before updating state
    collectionTimeoutRef.current = setTimeout(() => {
      setPhrases(currentPhrases => {
        const phrase = currentPhrases.find(p => p.id === id);
        if (phrase && !phrase.isSucked) {
          return currentPhrases.map(p =>
            p.id === id ? { ...p, isSucked: true } : p
          );
        }
        return currentPhrases;
      });
      
      // Add a cooldown before allowing the next collection
      setTimeout(() => {
        setCollecting(false);
        setLastCollectedId(null);
      }, 300); // Cooldown time in ms
    }, 200); // Collection animation time
  }, [collecting]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (collectionTimeoutRef.current) {
        clearTimeout(collectionTimeoutRef.current);
      }
    };
  }, []);

  // Handler for vacuum dragging - checks for collisions
  const handleVacuumDrag = useCallback((event, info) => {
    if (!vacuumRef.current || collecting) return;
    
    const vacuumRect = vacuumRef.current.getBoundingClientRect();
    const currentPhrases = phrases; // Capture current state

    // Store all overlapping phrases
    const overlappingPhrases = [];

    // Find all phrases that overlap with the vacuum
    phraseRefs.current.forEach((phraseRef, id) => {
      const currentPhraseState = currentPhrases.find(p => p.id === id);
      if (phraseRef.current && currentPhraseState && !currentPhraseState.isSucked) {
        const phraseRect = phraseRef.current.getBoundingClientRect();
        
        if (doRectsOverlap(vacuumRect, phraseRect)) {
          // Add to overlapping phrases
          overlappingPhrases.push({
            id,
            // Store DOM node index to determine visual stacking order
            domIndex: Array.from(phraseRef.current.parentNode.children).indexOf(phraseRef.current)
          });
        }
      }
    });

    // If we have overlapping phrases, choose the one visually on top
    if (overlappingPhrases.length > 0) {
      // Sort by DOM index (higher = rendered later = visually on top)
      overlappingPhrases.sort((a, b) => b.domIndex - a.domIndex);
      
      // Choose the top phrase (first one after sorting)
      const topPhraseId = overlappingPhrases[0].id;
      handlePhraseSucked(topPhraseId);
    }
  }, [handlePhraseSucked, phrases, collecting]); // Depend on phrases state

  // Reset function for Start Over button
  const handleReset = useCallback(() => {
    // Cancel any ongoing collection
    if (collectionTimeoutRef.current) {
      clearTimeout(collectionTimeoutRef.current);
    }
    setCollecting(false);
    setLastCollectedId(null);
    
    // First, reset the phrases
    setPhrases(initialPhrasesData.map(p => ({ ...p, isSucked: false })));
    
    // Then clear all refs to force recreation
    phraseRefs.current = new Map();
    
    // Reposition vacuum to upper left position
    if (vacuumRef.current) {
      const vacuumSize = isMobile ? MOBILE_VACUUM_SIZE : BASE_VACUUM_SIZE;
      // Use direct DOM manipulation for reliable positioning
      vacuumRef.current.style.left = `calc(${VACUUM_START_X}% - ${vacuumSize/2}px)`;
      vacuumRef.current.style.top = `${VACUUM_START_Y}%`;
      vacuumRef.current.style.transform = 'none';
    }
  }, [isMobile]);

  // Calculate sucked count directly from phrases state
  const suckedCount = phrases.filter(p => p.isSucked).length;
  
  // Calculate which lines to show and their progress
  const thresholdPerLine = TOTAL_PHRASES / TOTAL_LINES;
  const completedLines = Math.floor(suckedCount / thresholdPerLine);
  const currentLineIndex = Math.min(Math.floor(suckedCount / thresholdPerLine), TOTAL_LINES - 1);
  const nextLineIndex = Math.min(currentLineIndex + 1, TOTAL_LINES - 1);
  const currentLineProgress = (suckedCount % thresholdPerLine) / thresholdPerLine;

  // Check if a new phrase was collected and trigger line animation
  useEffect(() => {
    if (suckedCount > previousSuckedCount) {
      // A new phrase was collected
      if (suckedCount === 1 || Math.floor(previousSuckedCount / thresholdPerLine) < Math.floor(suckedCount / thresholdPerLine)) {
        // Either first phrase or we crossed a threshold to a new line
        setAnimatingLineIndex(Math.floor(suckedCount / thresholdPerLine));
        
        // Reset animation state after animation completes
        const timer = setTimeout(() => {
          setAnimatingLineIndex(null);
        }, 1000);
        
        return () => clearTimeout(timer);
      }
    }
    
    // Update previous count
    setPreviousSuckedCount(suckedCount);
  }, [suckedCount, previousSuckedCount, thresholdPerLine]);

  const activePhrases = phrases.filter(p => !p.isSucked);

  // Enhanced line variants with more sophisticated animation states
  const lineVariants = {
    hidden: { 
      opacity: 0, 
      y: 12, 
      color: "#a8a29e",
      transition: { duration: 0.3 } 
    },
    visible: { 
      opacity: 1, 
      y: 0, 
      color: "#2A2420",
      transition: { 
        duration: 0.5,
        ease: "easeOut"
      } 
    },
    animateIn: { 
      opacity: [0, 1], 
      y: [15, 0], 
      scale: [0.95, 1.02, 1],
      color: ["#a8a29e", "#2A2420"],
      transition: { 
        duration: 1.2,
        ease: "easeOut",
        times: [0, 1]
      } 
    },
    partial: (progress) => ({
      opacity: 0.3 + (progress * 0.7), // Start at 30% opacity and increase based on progress
      y: 6 - (progress * 6), // Start slightly below and move up based on progress
      color: progress < 0.5 ? "#a8a29e" : `rgba(42, 36, 32, ${progress})`, // Transition from gray to black
      transition: { duration: 0.4 }
    })
  };

  // Button animation variants
  const buttonVariants = {
    initial: { scale: 1 },
    hover: { 
      scale: 1.05, 
      boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
      transition: { duration: 0.25 } 
    },
    tap: { scale: 0.95, transition: { duration: 0.15 } }
  };

  // Set different heights based on viewport
  const containerHeight = 'auto'; // Changed from fixed height to auto
  const phraseAreaHeight = isMobile ? '45vh' : '50vh'; // Reduced heights to make room for poem

  return (
    <div 
      ref={containerRef} 
      className="relative w-full min-h-screen overflow-hidden font-serif flex flex-col items-center pb-16"
      style={{ 
        height: containerHeight, 
        minHeight: '100vh',
        background: "linear-gradient(to bottom, #f8f3ea, #eae6d9)",
        fontFamily: '"Georgia", "Times New Roman", serif'
      }}
    >
      {/* Title Area - Fixed at top with padding */}
      <div className="w-full border-b py-6 px-4 mb-6 z-30 text-center"
           style={{
             background: "rgba(245, 240, 231, 0.97)",
             borderColor: "#d3c7b6",
             boxShadow: "0 2px 8px rgba(0,0,0,0.07)"
           }}>
        <h1 className="text-3xl md:text-4xl font-serif mb-2"
            style={{
              color: "#42352A",
              fontWeight: "600",
              letterSpacing: "0.02em",
              fontFamily: '"Georgia", "Times New Roman", serif'
            }}>
          Self Portrait of a Schmutz Connoisseur
        </h1>
        <p className="text-sm md:text-base"
           style={{
             color: "#786d60",
             letterSpacing: "0.01em",
             fontWeight: "500"
           }}>
          Gather {TOTAL_PHRASES} pieces to compose the poem.
        </p>
      </div>

      {/* Content area with proper top margin to avoid overlap */}
      <div className="container mx-auto mt-2 md:mt-6 w-full max-w-4xl flex flex-col">
        {/* Phrase Area */}
        <div 
          className="relative w-full max-w-4xl mb-8" 
          style={{ height: phraseAreaHeight }}
        >
          <DraggableVacuum
              vacuumRef={vacuumRef}
              constraintsRef={containerRef}
              onDrag={handleVacuumDrag}
              isMobile={isMobile}
              isCollecting={collecting}
          />
          <AnimatePresence>
            {activePhrases.map(phrase => (
              <Phrase
                key={phrase.id}
                id={phrase.id}
                text={phrase.text}
                initialX={phrase.x}
                initialY={phrase.y}
                phraseRef={phraseRefs.current.get(phrase.id)}
                isMobile={isMobile}
                isBeingCollected={phrase.id === lastCollectedId}
              />
            ))}
          </AnimatePresence>
        </div>

        {/* Final Text Area */}
        <div className="relative w-full max-w-4xl flex-grow flex items-center justify-center text-center px-4 z-20">
          {/* Container for the lines */}
          <div className="w-full md:w-auto space-y-2 md:space-y-3 px-5 py-6 md:px-10 md:py-8 rounded-lg"
               style={{
                 background: "rgba(250, 247, 242, 0.95)",
                 border: "1px solid #d3c7b6",
                 boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
                 backdropFilter: "blur(3px)"
               }}>
            {FINAL_LINES.map((line, index) => {
              // Determine the animation state for each line
              let animationState;
              
              if (index === animatingLineIndex) {
                animationState = "animateIn";
              } else if (index < currentLineIndex) {
                animationState = "visible"; // Previous lines fully visible
              } else if (index === currentLineIndex) {
                // Current line - show with progress
                animationState = { 
                  opacity: 0.3 + (currentLineProgress * 0.7),
                  y: 6 - (currentLineProgress * 6),
                  color: currentLineProgress < 0.5 ? "#a8a29e" : `rgba(42, 36, 32, ${currentLineProgress})`,
                };
              } else {
                animationState = "hidden"; // Future lines hidden
              }
              
              return (
                <motion.p
                  key={index}
                  variants={lineVariants}
                  initial="hidden"
                  animate={animationState}
                  className="text-base md:text-lg"
                  style={{
                    fontFamily: '"Georgia", "Times New Roman", serif',
                    lineHeight: "1.6",
                    fontWeight: "500"
                  }}
                >
                  {line}
                </motion.p>
              );
            })}
          </div>
        </div>

        {/* Start Over Button - Centered below the poem */}
        <div className="w-full flex justify-center mt-8 mb-4">
          <motion.button
            onClick={handleReset}
            variants={buttonVariants}
            initial="initial"
            whileHover="hover"
            whileTap="tap"
            className="px-6 py-3 rounded-md z-30 text-sm md:text-base"
            style={{
              background: "#e9e0d0",
              color: "#5d4c3c",
              border: "1px solid #c7b9a4",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              fontFamily: '"Georgia", "Times New Roman", serif',
              fontWeight: "600",
              letterSpacing: "0.03em",
              transition: "all 0.25s ease"
            }}
          >
            Start Over
          </motion.button>
        </div>
      </div>
    </div>
  );
}

export default VacuumPoem;
